import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import HubSpotAccounts from "./HubSpotAccounts";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import { IconClose1 } from "@velaro/velaro-component-lib";

interface HubSpotConfig {
  id: number;
  siteId: number;
  displayName: string;
  hubId: string;
  portalName: string;
  pushTranscripts: boolean;
  createContactIfMissing: boolean;
  enabled: boolean;
  createdAt: string;
  accessTokenMasked: string;
}

const CAPABILITIES = [
  {
    title: "Contact lookup & sync",
    icon: "👤",
    items: [
      "Find any HubSpot contact instantly by email address — name, company, lifecycle stage, and open deals returned in seconds",
      "Retrieve the full contact record including custom properties, associated deals, and complete activity timeline",
      "Automatically create a new HubSpot contact when a visitor's email isn't found (configurable per connection)",
      "Push chat transcripts as timestamped Notes directly on the matched contact record"
    ]
  },
  {
    title: "Automated transcript push",
    icon: "📝",
    items: [
      "When a conversation ends, the full transcript is automatically pushed to HubSpot — no agent action required",
      "Attached as a Note on the matched contact so your sales team has complete chat context",
      "Configurable per connected HubSpot account (toggle in the Accounts tab)"
    ]
  },
  {
    title: "AI workflow tools",
    icon: "🤖",
    items: [
      "25 tools available to every AI workflow node on this site: contact management, company lookup, deals & pipeline, pipeline stages lookup, tickets & support, tasks, and team engagement",
      "No workflow configuration required — the AI decides when to call HubSpot based on conversation context",
      "Multiple HubSpot accounts supported — connect portals for different brands or regions on the same site"
    ]
  }
];

const COMING_SOON = [
  "Two-way sync via HubSpot webhooks — property changes in HubSpot flow back to Velaro contact fields",
  "Contact lifecycle stage updates (Lead → MQL → SQL → Customer) triggered from chat outcomes",
  "Marketing list enrollment based on conversation outcomes",
  "HubSpot form submission capture → inbound lead creation workflow"
];

const AI_TOOLS = [
  // Contact Management (5)
  {
    name: "hubspot_search_contact",
    label: "Search contact",
    group: "Contact Management",
    description: "Find a HubSpot contact by email or phone. Returns name, company, lifecycle stage, and deal info."
  },
  {
    name: "hubspot_get_contact",
    label: "Get contact details",
    group: "Contact Management",
    description: "Fetch full contact record including custom properties, associated deals, and activity history."
  },
  {
    name: "hubspot_update_contact",
    label: "Update contact",
    group: "Contact Management",
    description: "Update contact properties such as lifecycle stage, job title, phone, or owner."
  },
  {
    name: "hubspot_create_contact",
    label: "Create contact",
    group: "Contact Management",
    description: "Create a new HubSpot contact from visitor data collected during the conversation."
  },
  {
    name: "hubspot_search_contacts",
    label: "Advanced contact search",
    group: "Contact Management",
    description: "Multi-filter search across contacts — by name, company, owner, lifecycle stage, or date."
  },
  // Company (3)
  {
    name: "hubspot_search_companies",
    label: "Search companies",
    group: "Company",
    description: "Find companies in HubSpot by name or domain. Returns industry, size, and associated contacts."
  },
  {
    name: "hubspot_get_company",
    label: "Get company details",
    group: "Company",
    description: "Retrieve full company record including all properties, associated contacts, and linked deals."
  },
  {
    name: "hubspot_get_contact_companies",
    label: "Get contact's companies",
    group: "Company",
    description: "List all companies associated with a contact — useful for multi-company contacts."
  },
  // Deals & Pipeline (5)
  {
    name: "hubspot_get_deals",
    label: "Get contact's deals",
    group: "Deals & Pipeline",
    description: "List all deals associated with a contact — shows stage, amount, and close date."
  },
  {
    name: "hubspot_search_deals",
    label: "Search deals",
    group: "Deals & Pipeline",
    description: "Find deals by name, stage, pipeline, or associated contact."
  },
  {
    name: "hubspot_get_deal",
    label: "Get deal details",
    group: "Deals & Pipeline",
    description: "Retrieve full deal record: amount, pipeline stage, close date, and associated contacts."
  },
  {
    name: "hubspot_update_deal",
    label: "Update deal",
    group: "Deals & Pipeline",
    description: "Update a deal's stage, amount, close date, or owner from conversation data."
  },
  {
    name: "hubspot_create_deal",
    label: "Create deal",
    group: "Deals & Pipeline",
    description: "Create a new deal in HubSpot and associate it with a contact and company."
  },
  // Tickets & Support (5)
  {
    name: "hubspot_create_ticket",
    label: "Create ticket",
    group: "Tickets & Support",
    description: "Create a HubSpot Service Hub support ticket linked to the contact and conversation."
  },
  {
    name: "hubspot_get_ticket",
    label: "Get ticket details",
    group: "Tickets & Support",
    description: "Retrieve a support ticket by ID — status, priority, pipeline stage, and description."
  },
  {
    name: "hubspot_get_contact_tickets",
    label: "Get contact's tickets",
    group: "Tickets & Support",
    description: "List all support tickets associated with a contact — useful for repeat-issue detection."
  },
  {
    name: "hubspot_search_tickets",
    label: "Search tickets",
    group: "Tickets & Support",
    description: "Find tickets by subject, status, priority, or pipeline stage."
  },
  {
    name: "hubspot_update_ticket",
    label: "Update ticket",
    group: "Tickets & Support",
    description: "Update a ticket's status, priority, owner, or pipeline stage from the conversation."
  },
  // Tasks (2)
  {
    name: "hubspot_get_contact_tasks",
    label: "Get contact's tasks",
    group: "Tasks",
    description: "Retrieve open and completed tasks associated with a contact for follow-up context."
  },
  {
    name: "hubspot_create_task",
    label: "Create task",
    group: "Tasks",
    description: "Create a follow-up task in HubSpot — assigned to an owner with due date and subject."
  },
  // Engagement & Team (4)
  {
    name: "hubspot_add_note",
    label: "Add note to contact",
    group: "Engagement & Team",
    description: "Attach a note (e.g. conversation summary or transcript) to a contact's activity timeline."
  },
  {
    name: "hubspot_get_contact_timeline",
    label: "Get contact timeline",
    group: "Engagement & Team",
    description: "Retrieve recent engagements on a contact — emails, calls, meetings, and notes."
  },
  {
    name: "hubspot_get_owners",
    label: "Get HubSpot owners",
    group: "Engagement & Team",
    description: "List HubSpot users (owners/reps) — use to assign contacts, deals, or tickets to the right person."
  },
  {
    name: "hubspot_create_engagement",
    label: "Log call or meeting",
    group: "Engagement & Team",
    description: "Create an engagement record (call or meeting) on a contact to log conversation activity."
  },
  {
    name: "hubspot_get_pipeline_stages",
    label: "Get pipeline stages",
    group: "Deals & Pipeline",
    description: "Returns all deal and ticket pipeline stages with their IDs and labels. Use this before updating deal/ticket stages to get the correct stage ID for your pipeline."
  }
];

const AI_TOOL_GROUPS = ["Contact Management", "Company", "Deals & Pipeline", "Tickets & Support", "Tasks", "Engagement & Team"];

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));

  function toggleSection(i: number) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  }

  return (
    <div className="bg-white rounded-2xl shadow-md mb-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 rounded-2xl"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">
            {CAPABILITIES.length} features
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 16 16" fill="currentColor"
        >
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-6 pb-6">
          <div className="space-y-2 mt-4">
            {CAPABILITIES.map((cap, i) => (
              <div key={i} className="rounded-lg border border-slate-100 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection(i)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-base">{cap.icon}</span>
                    <span className="text-sm font-medium text-slate-700">{cap.title}</span>
                  </div>
                  <svg
                    className={`w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0 ${openSections.has(i) ? "rotate-180" : ""}`}
                    viewBox="0 0 16 16" fill="currentColor"
                  >
                    <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                  </svg>
                </button>
                {openSections.has(i) && (
                  <div className="px-4 pb-3 space-y-1.5 border-t border-slate-50">
                    {cap.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2 pt-1.5">
                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                        <span className="text-xs text-slate-600">{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 rounded-lg border border-dashed border-slate-200 p-4">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Coming soon</div>
            <div className="space-y-1.5">
              {COMING_SOON.map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-slate-300 flex-shrink-0 text-xs mt-0.5">○</span>
                  <span className="text-xs text-slate-400">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function HubSpot() {
  const api = useApi();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<HubSpotConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"setup" | "logs">("setup");
  const [showWizard, setShowWizard] = useState(false);

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("HubSpot/Configs");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const hasActiveConfig = configs.some((c) => c.enabled);

  return (
    <div>
      {showWizard && (
        <StarterWorkflowWizard
          integration="hubspot"
          hubspotConfigId={configs.find((c) => c.enabled)?.id}
          onClose={() => setShowWizard(false)}
        />
      )}

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-4">
        {(["setup", "logs"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            className={`py-2 mr-4 text-sm cursor-pointer capitalize ${activeTab === tab ? "border-b-2 border-cornflower-blue-500 text-cornflower-blue-600 font-medium" : "text-slate-500"}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "setup" ? "Setup" : "Activity Log"}
          </button>
        ))}
      </div>

      {activeTab === "logs" && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <IntegrationActivityLog integrationSlug="HubSpot" />
        </div>
      )}

      {activeTab === "setup" && (
        <div>
          {/* Capabilities — always visible */}
          <CapabilitiesPanel />

          {loading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : (
            <>
              <div className="mb-6">
                <HubSpotAccounts
                  configs={configs}
                  onDelete={(id) => setConfigs((prev) => prev.filter((c) => c.id !== id))}
                  onRefresh={fetchConfigs}
                />
              </div>

              {/* AI Tools panel */}
              {configs.length > 0 && (
                <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="font-semibold text-slate-800 text-lg flex items-center gap-2">
                        AI Tools
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasActiveConfig ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                          {hasActiveConfig ? "Active" : "No active connection"}
                        </span>
                      </div>
                      <div className="text-sm text-slate-500 mt-0.5">
                        25 tools available to any AI workflow node on this site. No configuration required — the AI decides when to call HubSpot based on conversation context.
                      </div>
                    </div>
                    {hasActiveConfig && (
                      <button
                        type="button"
                        onClick={() => setShowWizard(true)}
                        className="text-sm px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 whitespace-nowrap font-medium shadow-sm"
                      >
                        Launch Starter Wizard
                      </button>
                    )}
                  </div>

                  <div className="border-t border-slate-100 pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                        Available AI tools
                      </div>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">25 tools</span>
                    </div>
                    {AI_TOOL_GROUPS.map((group) => (
                      <div key={group} className="mb-4">
                        <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">{group}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                            <div
                              key={tool.name}
                              className={`flex gap-3 p-3 rounded-lg border ${hasActiveConfig ? "border-green-200 bg-green-50" : "border-slate-200 bg-slate-50"}`}
                            >
                              <div className={`mt-0.5 w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${hasActiveConfig ? "bg-green-500" : "bg-slate-300"}`}>
                                {hasActiveConfig && (
                                  <svg viewBox="0 0 10 10" width="8" height="8" fill="none">
                                    <path d="M1.5 5L3.5 7L8.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-slate-700">{tool.label}</div>
                                <div className="text-xs text-slate-500 mt-0.5">{tool.description}</div>
                                <code className="text-[10px] text-slate-400 mt-1 block">{tool.name}</code>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transcript push status */}
              {configs.some((c) => c.pushTranscripts && c.enabled) && (
                <div className="bg-white rounded-2xl shadow-md p-6">
                  <div className="font-semibold text-slate-800 text-lg mb-2">Transcript Push</div>
                  <div className="text-sm text-slate-500 mb-3">
                    Enabled on {configs.filter((c) => c.pushTranscripts && c.enabled).length} connection(s). Chat transcripts are automatically pushed to HubSpot as Notes when conversations end.
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
                    ✓ Auto-push active — transcripts will appear on the matched HubSpot contact after each chat.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function BackToIntegrationLandingHubSpot() {
  const navigate = useNavigate();
  return (
    <div className="flex cursor-pointer" onClick={() => navigate("/integrations")}>
      <IconClose1 className="h-6 w-6" />
    </div>
  );
}
